/**
 * Shared UI primitives for the Volunteer Community dashboard pages.
 * Gov-styled (white rounded cards), WCAG-minded (44px targets, aria-hidden on
 * decorative icons). No financial concepts anywhere. Real data only — metrics
 * without a backend source use the "Data required" treatment, never a fabricated
 * number with a verified gradient.
 */
import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapPin, Users, Clock, Award, Loader2, CalendarDays, Megaphone, Share2 } from 'lucide-react'
import { cn, formatDate, getInitials } from '@/lib/utils'
import { GovCard, GovEmptyState } from '@/components/layout/GovLayout'
import type {
  CommunityRole, EventResponse, MemberResponse, AnnouncementResponse,
  BadgeResponse, MemberBadgeResponse, ActivityResponse, CommunityCampaignResponse,
} from '@/services/communityApi'

// ──────────────────────────────────────────────────────────────────────────
// Section states
// ──────────────────────────────────────────────────────────────────────────
export function SectionLoading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3" aria-busy="true">
      <span className="sr-only">Loading</span>
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
      ))}
    </div>
  )
}

export function SectionEmpty({ icon, title, description }: { icon: string; title: string; description?: string }) {
  return <GovEmptyState icon={icon} title={title} description={description} />
}

// ──────────────────────────────────────────────────────────────────────────
// Pills
// ──────────────────────────────────────────────────────────────────────────
const ROLE_STYLE: Record<CommunityRole, string> = {
  LEADER: 'bg-blue-100 text-blue-700',
  COORDINATOR: 'bg-indigo-100 text-indigo-700',
  MODERATOR: 'bg-violet-100 text-violet-700',
  VOLUNTEER: 'bg-emerald-100 text-emerald-700',
  MEMBER: 'bg-gray-100 text-gray-700',
}
export function RoleBadge({ role }: { role: CommunityRole }) {
  const { t } = useTranslation()
  return (
    <span className={cn('inline-block text-xs font-medium px-2 py-0.5 rounded-full', ROLE_STYLE[role] ?? ROLE_STYLE.MEMBER)}>
      {t(`community.role.${role}`, role)}
    </span>
  )
}

const STATUS_STYLE: Record<string, string> = {
  UPCOMING: 'bg-blue-100 text-blue-700', ONGOING: 'bg-emerald-100 text-emerald-700',
  COMPLETED: 'bg-gray-100 text-gray-600', CANCELLED: 'bg-red-100 text-red-700',
  ACTIVE: 'bg-emerald-100 text-emerald-700', PENDING: 'bg-amber-100 text-amber-700',
  REVIEWED: 'bg-amber-100 text-amber-700', RESOLVED: 'bg-emerald-100 text-emerald-700',
  DISMISSED: 'bg-gray-100 text-gray-600',
}
export function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation()
  return (
    <span className={cn('inline-block text-xs font-medium px-2 py-0.5 rounded-full', STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600')}>
      {t(`community.status.${status}`, status)}
    </span>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Metric tile — real numbers only; "Data required" where no source exists.
// ──────────────────────────────────────────────────────────────────────────
export function MetricTile({ icon: Icon, value, labelKey, dataRequired }: {
  icon: React.ComponentType<{ className?: string }>
  value: number
  labelKey: string
  dataRequired?: boolean
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cn('text-2xl font-bold', dataRequired ? 'text-gray-400' : 'text-gray-900')}>
            {dataRequired ? '—' : value}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{t(labelKey)}</p>
        </div>
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
        </div>
      </div>
      {dataRequired && (
        <span className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
          {t('community.metrics.dataRequired')}
        </span>
      )}
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Avatar
// ──────────────────────────────────────────────────────────────────────────
export function Avatar({ name, image, size = 40 }: { name: string; image?: string | null; size?: number }) {
  if (image) return <img src={image} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
  return (
    <div className="rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }} aria-hidden="true">
      {getInitials(name)}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Event card
// ──────────────────────────────────────────────────────────────────────────
export function EventCard({ event, onRegister, onCancel, busy }: {
  event: EventResponse
  onRegister?: (e: EventResponse) => void
  onCancel?: (e: EventResponse) => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  const full = event.capacity > 0 && event.registeredCount >= event.capacity
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-blue-50 flex flex-col items-center justify-center shrink-0">
          <CalendarDays className="w-5 h-5 text-blue-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{event.title}</h3>
            <StatusPill status={event.status} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{t(`community.events.type.${event.type}`, event.type)}</p>
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{event.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />{formatDate(event.startDate)}</span>
            {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />{event.location}</span>}
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" aria-hidden="true" />{event.registeredCount}/{event.capacity || '∞'}</span>
          </div>
          {onRegister && (
            <div className="mt-3">
              {event.myRegistered ? (
                <button onClick={() => onCancel?.(event)} disabled={busy}
                  className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50 min-h-[40px]">
                  {t('community.actions.cancelRegistration')}
                </button>
              ) : (
                <button onClick={() => onRegister(event)} disabled={busy || full}
                  className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
                  {full ? t('community.events.capacity') : t('community.actions.register')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Campaign card (non-financial)
// ──────────────────────────────────────────────────────────────────────────
export function CampaignCard({ campaign, onJoin, onShare, busy }: {
  campaign: CommunityCampaignResponse
  onJoin?: (c: CommunityCampaignResponse) => void
  onShare?: (c: CommunityCampaignResponse) => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
          <Megaphone className="w-5 h-5 text-emerald-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{campaign.title}</h3>
            <StatusPill status={campaign.status} />
          </div>
          {campaign.objective && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{campaign.objective}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            {campaign.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />{campaign.location}</span>}
            {campaign.organizerName && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" aria-hidden="true" />{campaign.organizerName}</span>}
            {campaign.volunteersNeeded != null && <span>{t('community.campaigns.volunteersNeeded')}: {campaign.volunteersNeeded}</span>}
            <span>{t('community.campaigns.participants')}: {campaign.participantsCount}</span>
          </div>
          <div className="mt-3 flex items-center gap-4">
            {onJoin && (
              <button onClick={() => onJoin(campaign)} disabled={busy || campaign.myParticipation}
                className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
                {campaign.myParticipation ? t('community.campaigns.joined') : t('community.actions.join')}
              </button>
            )}
            {onShare && (
              <button onClick={() => onShare(campaign)} disabled={busy}
                className="text-sm font-medium text-gray-600 hover:underline disabled:opacity-50 min-h-[40px] inline-flex items-center gap-1">
                <Share2 className="w-4 h-4" aria-hidden="true" />{t('community.actions.share')}
              </button>
            )}
          </div>
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Member card (PII-safe — no email/phone)
// ──────────────────────────────────────────────────────────────────────────
export function MemberCard({ member, onSetRole, canManage, busy }: {
  member: MemberResponse
  onSetRole?: (m: MemberResponse) => void
  canManage?: boolean
  busy?: boolean
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <Avatar name={member.user.fullName} image={member.user.profileImage} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{member.user.fullName}</h3>
            <RoleBadge role={member.role} />
          </div>
          {member.skills?.length > 0 && (
            <p className="text-xs text-gray-500 mt-1"><span className="font-medium">{t('community.members.skills')}:</span> {member.skills.join(', ')}</p>
          )}
          {member.interests?.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5"><span className="font-medium">{t('community.members.interests')}:</span> {member.interests.join(', ')}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" aria-hidden="true" />{member.badgeCount} {t('community.members.badges')}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{member.activityCount} {t('community.members.activity')}</span>
          </div>
          {canManage && onSetRole && (
            <button onClick={() => onSetRole(member)} disabled={busy}
              className="mt-2 text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
              {t('community.members.setRole')}
            </button>
          )}
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Announcement card
// ──────────────────────────────────────────────────────────────────────────
export function AnnouncementCard({ announcement, onPin, onDelete, canManage, busy }: {
  announcement: AnnouncementResponse
  onPin?: (a: AnnouncementResponse) => void
  onDelete?: (a: AnnouncementResponse) => void
  canManage?: boolean
  busy?: boolean
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
          <Megaphone className="w-5 h-5 text-amber-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
            {announcement.pinned && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{t('community.announcements.pinned')}</span>}
          </div>
          <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{announcement.body}</p>
          <p className="text-xs text-gray-400 mt-2">{t('community.announcements.createdBy')} {announcement.createdBy.fullName} · {formatDate(announcement.createdAt)}</p>
          {canManage && (onPin || onDelete) && (
            <div className="mt-2 flex items-center gap-4">
              {onPin && <button onClick={() => onPin(announcement)} disabled={busy} className="text-sm font-medium text-gray-600 hover:underline disabled:opacity-50 min-h-[40px]">{announcement.pinned ? t('community.announcements.unpin') : t('community.announcements.pin')}</button>}
              {onDelete && <button onClick={() => onDelete(announcement)} disabled={busy} className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50 min-h-[40px]">{t('community.announcements.delete')}</button>}
            </div>
          )}
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Badge card
// ──────────────────────────────────────────────────────────────────────────
const TIER_STYLE: Record<string, string> = {
  Gold: 'from-amber-400 to-yellow-500', Silver: 'from-slate-300 to-slate-400', Bronze: 'from-orange-400 to-amber-600',
}
export function BadgeCard({ badge, earned }: { badge: BadgeResponse; earned?: boolean }) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className={cn('w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center text-2xl shrink-0', TIER_STYLE[badge.tier] ?? 'from-gray-300 to-gray-400', !earned && 'opacity-40 grayscale')}>
          <span aria-hidden="true">{badge.icon}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900">{badge.name}</h3>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t(`community.recognition.tier.${badge.tier}`, badge.tier)}</span>
            {earned !== undefined && (
              <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full', earned ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
                {earned ? t('community.recognition.earned') : t('community.recognition.notEarned')}
              </span>
            )}
          </div>
          {badge.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{badge.description}</p>}
          {badge.criteria && (
            <p className="text-xs text-gray-500 mt-1">
              <span className="font-medium text-gray-600">{t('community.recognition.howToEarn', 'How to earn')}:</span> {badge.criteria}
            </p>
          )}
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Activity timeline item
// ──────────────────────────────────────────────────────────────────────────
export function ActivityItem({ activity }: { activity: ActivityResponse }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-700">{activity.description}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {activity.user ? <>{activity.user.fullName} · </> : null}
          {t(`community.activities.type.${activity.type}`, activity.type)} · {formatDate(activity.createdAt)}
        </p>
      </div>
    </div>
  )
}

// Re-export for convenience.
export { GovCard, GovEmptyState, Loader2 }
export type { ReactNode }